import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, concat, finalize, Observable, shareReplay } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { defaultProject, PostProject, Project, PutProject } from 'src/app/interfaces/api/project';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../core/api.service';
import { LocalStorageService } from '../../core/local-storage.service';
import { RequestService } from '../../core/request.service';
import { WebSocketService } from '../../core/web-socket.service';

type QueryParams = Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
  providedIn: 'root'
})

export class ProjectService implements OnDestroy {
  // Services
  private readonly localStorage: LocalStorageService = inject(LocalStorageService);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly router: Router = inject(Router);
  private readonly pop: Pop = inject(Pop);
  // RequestService et WebSocketService injectent déjà ProjectService (projet courant,
  // room active) : une injection directe ici créerait une dépendance circulaire Angular
  // (NG0200). Résolues à la demande via l'Injector — seulement au premier appel réel
  // (voir les getters request/socket plus bas), bien après que tout soit construit.
  private readonly injector: Injector = inject(Injector);
  private _request?: RequestService;
  private _socket?: WebSocketService;

  // Variables
  collection: string = "Project";
  collectionPath: string = "projects";
  projects: Project[] = [];
  apiUrl: string = environment.API_URL;
  private channels: Map<string, BroadcastChannel> = new Map();
  // Un flux partagé par query (2 composants avec la même query = 1 seul GET + 1 seul
  // abonnement socket). Nettoyé automatiquement (finalize) quand plus personne n'écoute,
  // pour ne pas accumuler des entrées mortes au fil de la session.
  private readonly liveProjects = new Map<string, Observable<Project[]>>();

  // Signals
  currentProject: BehaviorSubject<Project> = new BehaviorSubject<Project>(defaultProject);
  currentProjectSignal: WritableSignal<Project> = signal(defaultProject);
  private _isLoadingProject: WritableSignal<boolean> = signal<boolean>(false);
  readonly isLoadingProject: Signal<boolean> = this._isLoadingProject.asReadonly();

  constructor() {
    this.cleanupOldClosingKeys();

    const sessionProject = sessionStorage.getItem('project');
    const project: any = sessionProject ? JSON.parse(sessionProject) : this.localStorage.getItem('project');

    if (project) {
      this.currentProject.next(project);
      this.currentProjectSignal.set(project);
      this.setupProjectUsageListener(project.database);
    }

    this.setupTabCloseListener();

    // setTimeout : laisse le graphe de DI Angular finir de se construire avant de
    // résoudre WebSocketService (voir commentaire sur `injector` plus haut) — sinon
    // cycle circulaire au démarrage.
    setTimeout(() => this.listenForActiveProjectUpdates());
  }

  private get request(): RequestService {
    if (!this._request) {
      this._request = this.injector.get(RequestService);
    }

    return this._request;
  }

  private get socket(): WebSocketService {
    if (!this._socket) {
      this._socket = this.injector.get(WebSocketService);
    }

    return this._socket;
  }

  //---REALTIME---//

  /**
   * Tient le projet ACTIF à jour en temps réel : si un update Project reçu
   * correspond au projet actuellement ouvert (currentProjectValue.id), les champs
   * sont fusionnés dans currentProject/currentProjectSignal. Aucun recâblage
   * nécessaire au changement de projet : la comparaison se fait à la volée à
   * chaque message reçu, donc c'est toujours le projet actif du moment qui profite
   * du temps réel — jamais les autres.
   */
  private listenForActiveProjectUpdates(): void {
    this.socket.getUpdateRaw(this.collection).subscribe({
      next: (update: any) => {
        if (!update.data) {
          return;
        }

        const activeId = this.currentProjectValue.id;
        if (!activeId) {
          return;
        }

        const data = Array.isArray(update.data) ? update.data : [update.data];

        if (update.method === 'DELETE') {
          const deletedIds = data.map((d: any) => d.id || d._id);

          if (deletedIds.includes(activeId)) {
            this.pop.warning('Le projet actif a été supprimé par un autre utilisateur');
            this.deleteCurrentProject();
            this.router.navigate(['/']);
          }

          return;
        }

        const updated = data.find((p: any) => (p.id || p._id) === activeId);

        if (updated) {
          this.mergeIntoCurrentProject(updated);
        }
      },
      error: (err) => console.error('❌ WebSocket update error (currentProject):', err)
    });
  }

  private mergeIntoCurrentProject(updated: Partial<Project>): void {
    const merged: Project = { ...this.currentProjectValue, ...updated };

    sessionStorage.setItem('project', JSON.stringify(merged));
    this.localStorage.setItem('project', merged);

    this.currentProject.next(merged);
    this.currentProjectSignal.set(merged);
  }

  //---GET---//

  /**
   * Flux toujours vivant : émission initiale (cache/API) puis chaque mise à jour
   * temps réel, exactement comme getUnits()/getTrackers(). Project est déclaré
   * 'global' dans collection-scope.config.ts, donc ce flux survit aux changements
   * de projet actif (contrairement aux collections 'tenant').
   *
   * Partagé (shareReplay) par query : 2 composants qui appellent getProjects()
   * avec la même query ne déclenchent qu'un seul GET + un seul abonnement socket.
   */
  getProjects(query: QueryParams = {}): Observable<Project[]> {
    const key = JSON.stringify(query);

    if (!this.liveProjects.has(key)) {
      const live$ = concat(
        this.request.getCollection(this.collection, this.collectionPath, query),
        this.socket.getUpdate(this.collection)
      ).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => this.liveProjects.delete(key))
      );

      this.liveProjects.set(key, live$);
    }

    return this.liveProjects.get(key)!;
  }

  getProjectById(id: string, key: string | null = null): Observable<Project> {
    let options: { headers?: HttpHeaders } = {};
    if (key) {
      options["headers"] = new HttpHeaders({
        'x-access-key': key,
        'x-project-id': id
      })
    }

    return this.apiService.getCollectionItem(this.collectionPath, "", id, options);
  }

  searchAndCountProjects(search: string, query: QueryParams = {}): Observable<{ results: Project[], total: number }> {
    return this.apiService.getCollection(this.collectionPath, 'searchAndCount', { q: search, ...query });
  }

  getInfos(query: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'infos', query);
  }

  getActivity(query: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'activity', query);
  }

  getAllProjectByUser(query: any): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, '', query);
  }

  getProjectListByUser(query: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'light', query);
  }

  getStatus(databases: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'status', { databases });
  }
  getTrackerStatus(databases: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'tracker-status', { databases });
  }

  getPreviews(databases: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'previews', { databases });
  }

  getProjectDatabase(): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'databases');
  }

  getDatabaseNames(query?: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'dBnames', query);
  }

  getProjectsWithReportsStatus(query?: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'reportsStatus', query);
  }

  //---POST---//

  addProject(project: PostProject): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, "", project);
  }

  /**
   * Heartbeat presence for a broadcaster the user is watching; the backend
   * accumulates today's per-broadcaster usage (deduped across viewers) and
   * returns it. Omit broadcaster/viewerId to just fetch today's credits.
   */
  reportStreamUsage(projectId: string, room?: string, broadcaster?: string, viewerId?: string, stop?: boolean): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, "streamUsage", { projectId, room, broadcaster, viewerId, stop });
  }

  //---PUT---//

  setProject(project: PutProject): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, "", project);
  }

  updateUserAccess(userId: string): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'updateUserAccess', { user: userId });
  }

  updateReportsStatus(reportsEnabled: boolean, databases: string[] = []): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'reportsStatus', { reportsEnabled, databases });
  }

  updateMailingLists(addToProjects: string[], removeFromProjects: string[] = []): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'mailingLists', { addToProjects, removeFromProjects });
  }

  //---DELETE---//

  deleteProject(id: string, freeze: boolean = true): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath, id, { freeze });
  }

  //---STORE---//

  get currentProjectValue(): Project {
    return this.currentProjectSignal();
  }

  startLoading(): void {
    this._isLoadingProject.set(true);
  }

  stopLoading(): void {
    this._isLoadingProject.set(false);
  }

  setCurrentProject(project: Project) {
    const previousProject = this.currentProjectValue;

    sessionStorage.setItem('project', JSON.stringify(project));
    sessionStorage.setItem('db', project.database);

    this.localStorage.setItem('project', project);
    this.localStorage.setItem('db', project.database);

    this.currentProject.next(project);
    this.currentProjectSignal.set(project);

    if (previousProject && previousProject.database !== project.database) {
      const oldChannel = this.channels.get(previousProject.database);

      if (oldChannel) {
        oldChannel.close();
        this.channels.delete(previousProject.database);
      }

      setTimeout(() => {
        this.checkAndCleanProjectIfUnused(previousProject.database);
      }, 500);

      this.setupProjectUsageListener(project.database);
    } else if (!previousProject) {
      this.setupProjectUsageListener(project.database);
    }
  }

  getProjectImageUrl(picture: string | null | undefined, mode: 'picture' | 'thumbnail'): string {
    const pictureUrl = `https://unifield.blob.core.windows.net/user-picture/`;
    const thumbnail = `https://unifield.blob.core.windows.net/map-picture/`;
    const defaultImage = '/img/default_project.png';

    if (!picture) {
      return defaultImage;
    }

    if (picture.includes('user_default.png') || picture.includes('default')) {
      return defaultImage;
    }

    if (picture.startsWith('http') || picture.startsWith('/')) {
      return picture;
    }

    if (mode === 'thumbnail') {
      return thumbnail + picture;
    }

    return pictureUrl + picture;
  }

  private setupProjectUsageListener(database: string): void {
    const existingChannel = this.channels.get(database);
    if (existingChannel) {
      existingChannel.close();
    }

    const channel = new BroadcastChannel(`project_${database}`);
    this.channels.set(database, channel);


    channel.addEventListener('message', (event) => {
      if (event.data.type === 'CHECK_USAGE' && event.data.database === database) {
        const currentSessionProject = sessionStorage.getItem('project');

        if (currentSessionProject) {
          const sessionProject = JSON.parse(currentSessionProject);

          if (sessionProject.database === database) {
            channel.postMessage({
              type: 'USAGE_RESPONSE',
              database: database
            });
          }
        }
      } else if (event.data.type === 'TAB_CLOSING' && event.data.database === database) {
        setTimeout(() => {
          this.checkAndCleanProjectIfUnused(database);
        }, 1500);
      }
    });
  }

  private setupTabCloseListener(): void {
    window.addEventListener('beforeunload', () => {
      const currentProject = sessionStorage.getItem('project');
      if (currentProject) {
        const project = JSON.parse(currentProject);

        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith(`closing_${project.database}_`)) {
            localStorage.removeItem(key);
          }
        }
      }
    });

    window.addEventListener('pagehide', () => {
      const currentProject = sessionStorage.getItem('project');

      if (currentProject) {
        const project = JSON.parse(currentProject);

        const channel = this.channels.get(project.database);
        if (channel) {
          channel.postMessage({
            type: 'TAB_CLOSING',
            database: project.database
          });

          channel.close();
          this.channels.delete(project.database);
        }

        const closingKey = `closing_${project.database}_${Date.now()}`;
        localStorage.setItem(closingKey, 'true');
      }
    });

    window.addEventListener('storage', (event) => {
      if (event.key && event.key.startsWith('closing_') && event.newValue === 'true') {
        const keyParts = event.key.split('_');
        keyParts.shift();
        keyParts.pop();
        const database = keyParts.join('_');

        if (event.key) {
          localStorage.removeItem(event.key);
        }

        setTimeout(() => {
          this.checkAndCleanProjectIfUnused(database);
        }, 1500);
      }
    });
  }

  private checkAndCleanProjectIfUnused(database: string): void {
    const currentSessionProject = sessionStorage.getItem('project');

    if (currentSessionProject) {
      const sessionProject = JSON.parse(currentSessionProject);
      if (sessionProject.database === database) {
        return;
      }
    }

    const channel = new BroadcastChannel(`project_${database}`);
    let tabCount: number = 0;
    let timeout: ReturnType<typeof setTimeout>;

    const responseHandler = (event: MessageEvent) => {
      if (event.data.type === 'USAGE_RESPONSE' && event.data.database === database) {
        tabCount++;
      }
    };

    channel.addEventListener('message', responseHandler);
    channel.postMessage({ type: 'CHECK_USAGE', database });

    timeout = setTimeout(() => {
      channel.removeEventListener('message', responseHandler);

      if (tabCount === 0) {
        this.localStorage.clearProject(database);
      }

      channel.close();
    }, 2000);
  }

  private cleanupOldClosingKeys(): void {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);

      if (key && key.startsWith('closing_')) {
        localStorage.removeItem(key);
      }
    }
  }

  ngOnDestroy(): void {
    this.channels.forEach(channel => channel.close());
    this.channels.clear();
  }

  resetCurrentProject() {
    this.currentProject.next(defaultProject);
    this.currentProjectSignal.set(defaultProject);
  }

  deleteCurrentProject() {
    const previousProject = this.currentProjectValue;
    sessionStorage.removeItem('project');
    sessionStorage.removeItem('db');
    this.localStorage.removeItem('project');
    this.localStorage.removeItem('db');

    if (previousProject && previousProject.database) {
      const channel = this.channels.get(previousProject.database);

      if (channel) {
        channel.close();
        this.channels.delete(previousProject.database);
      }

      this.localStorage.clearProject(previousProject.database);
    }

    this.currentProject.next(defaultProject);
    this.currentProjectSignal.set(defaultProject);
  }
}
